/**
 * useMemberImage Hook
 * Handles member profile image loading with automatic retry on blob URL failures.
 * Fixes Firefox issue where revoked blob URLs cause "Security Error" on reload.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { membersService } from '../services/members.service';

interface UseMemberImageOptions {
  /** Trigger re-fetch when this value changes (e.g., dataUpdatedAt timestamp) */
  refreshTrigger?: number;
}

interface UseMemberImageResult {
  /** The blob URL for the image, or null if not loaded/unavailable */
  imageUrl: string | null;
  /** Whether the image is currently being loaded */
  isLoading: boolean;
  /** Callback to pass to img onError - triggers automatic retry */
  handleImageError: () => void;
}

/**
 * Hook for loading member profile images with automatic error recovery.
 *
 * @param memberId - The member's ID, or null if no member selected
 * @param hasProfilePicture - Whether the member has a profile picture (default: true)
 * @param options - Additional options for controlling image loading
 * @returns Object with imageUrl, isLoading state, and handleImageError callback
 *
 * @example
 * ```tsx
 * const { imageUrl, isLoading, handleImageError } = useMemberImage(
 *   member.id,
 *   member.has_profile_picture
 * );
 *
 * return (
 *   <img
 *     src={imageUrl || '/placeholder.png'}
 *     onError={handleImageError}
 *   />
 * );
 * ```
 */
export function useMemberImage(
  memberId: number | null,
  hasProfilePicture: boolean = true,
  options: UseMemberImageOptions = {}
): UseMemberImageResult {
  const { refreshTrigger } = options;

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Track previous memberId to clear image only when switching to a different member
  const prevMemberIdRef = useRef<number | null>(null);

  // Clear image only when memberId changes (not on refreshTrigger changes)
  // This prevents flickering when filters change but same members are shown
  useEffect(() => {
    if (prevMemberIdRef.current !== null && prevMemberIdRef.current !== memberId) {
      setImageUrl(null);
    }
    prevMemberIdRef.current = memberId;
  }, [memberId]);

  // Load image when memberId, hasProfilePicture, refreshTrigger, or retryCount changes
  useEffect(() => {
    if (!memberId || !hasProfilePicture) {
      setImageUrl(null);
      setIsLoading(false);
      return;
    }

    let mounted = true;
    // Don't clear imageUrl here - keep showing current image while loading new one
    // This implements "stale-while-revalidate" pattern to prevent flickering
    setIsLoading(true);

    const loadImage = async () => {
      // If retrying, clear the stale cache first
      if (retryCount > 0) {
        membersService.revokeMemberImage(memberId);
      }

      const blobUrl = await membersService.fetchMemberImage(memberId);

      if (mounted) {
        setImageUrl(blobUrl);
        setIsLoading(false);
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [memberId, hasProfilePicture, refreshTrigger, retryCount]);

  // Reset retry count when member changes
  useEffect(() => {
    setRetryCount(0);
  }, [memberId]);

  // Called by img onError - triggers retry (max 1 retry to avoid infinite loops)
  const handleImageError = useCallback(() => {
    if (retryCount < 1 && memberId) {
      // Clear the potentially stale blob URL from cache
      membersService.revokeMemberImage(memberId);
      // Trigger re-fetch
      setRetryCount(prev => prev + 1);
    }
  }, [memberId, retryCount]);

  return { imageUrl, isLoading, handleImageError };
}
